import { gql, useMutation } from "@apollo/client";
import {
  Button,
  CircularProgress,
  Box,
  Stack,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import React, { useCallback, useRef, useState } from "react";
import { changePassword } from "../../../apollo/server";
import FlashMessage from "../../FlashMessage";
import LockIcon from "@mui/icons-material/Lock";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

const CHANGE_PASSWORD = gql`${changePassword}`;

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "#f9fafb",
    "& fieldset": { borderColor: "#e5e7eb" },
    "&:hover fieldset": { borderColor: "#d1d5db" },
    "&.Mui-focused fieldset": { borderColor: "#FF3008" },
  },
};

function PasswordCard() {
  const { t } = useTranslation();
  const formRef = useRef(null);
  const [passError, setPassError] = useState("");
  const [confirmError, setConfirmError] = useState("");
  const [error, setError] = useState({});
  const [mutate, { loading }] = useMutation(CHANGE_PASSWORD, { onError, onCompleted });
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);

  function onError(e) { setError({ type: "error", message: e.message }); }
  function onCompleted(data) {
    if (data.changePassword) {
      clearErrors();
      setError({ type: "success", message: "Password updated" });
      formRef.current.reset();
    } else {
      setError({ type: "error", message: "Invalid password" });
    }
  }

  const clearErrors = useCallback(() => {
    setPassError("");
    setConfirmError("");
    setError({});
  }, []);

  const handleAction = () => {
    clearErrors();
    const oldPassword = formRef.current["currentPassword"].value;
    const newPassword = formRef.current["newPassword"].value;

    if (!oldPassword) { setPassError("Current password is required"); return; }
    const passwordRegex = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}$/;
    if (!passwordRegex.test(newPassword)) {
      setConfirmError("Min 8 characters, with uppercase, lowercase, and a number");
      return;
    }
    mutate({ variables: { oldPassword, newPassword } });
  };

  const toggleSnackbar = useCallback(() => setError({}), []);

  return (
    <Box sx={{ width: "100%" }}>
      <FlashMessage
        open={Boolean(error.type)}
        severity={error.type}
        alertMessage={error.message}
        handleClose={toggleSnackbar}
      />
      <form ref={formRef}>
        <Stack gap={2}>
          <TextField
            name="currentPassword"
            label={t("currentPassword") || "Current Password"}
            type={showCurrent ? "text" : "password"}
            error={Boolean(passError)}
            helperText={passError}
            fullWidth
            size="small"
            sx={fieldSx}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><LockIcon sx={{ color: "#6b7280", fontSize: 18 }} /></InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowCurrent(v => !v)} edge="end">
                    {showCurrent ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            name="newPassword"
            label={t("newPassword") || "New Password"}
            type={showNew ? "text" : "password"}
            error={Boolean(confirmError)}
            helperText={confirmError || "Min 8 chars · 1 uppercase · 1 lowercase · 1 number"}
            fullWidth
            size="small"
            autoComplete="new-password"
            sx={fieldSx}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><LockIcon sx={{ color: "#6b7280", fontSize: 18 }} /></InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowNew(v => !v)} edge="end">
                    {showNew ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.5 }}>
            <Button
              disableElevation
              disabled={loading}
              variant="contained"
              onClick={(e) => { e.preventDefault(); handleAction(); }}
              sx={{
                backgroundColor: "#FF3008",
                color: "#fff",
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "10px",
                px: 3, py: 1,
                minWidth: 140,
                "&:hover": { backgroundColor: "#e02b00" },
              }}
            >
              {loading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Update Password"}
            </Button>
          </Box>
        </Stack>
      </form>
    </Box>
  );
}

export default React.memo(PasswordCard);
